import { marketingPricingFaqCss } from '../internal/marketingPricingFaq.js';
import {
  forwardRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ElementType,
  type FocusEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { T } from '@territory/tokens';
import { wedgeRole } from '../internal/wedgeRoles.js';
import { DENSE_PHONE, control, duration, font, radius, space, TOUCH_MIN, weight } from '../scale.js';
import { useStyleOnce } from '../internal/useStyleOnce.js';
import { usePrefersReducedMotion } from '../internal/usePrefersReducedMotion.js';
import { focusRing } from '../internal/focusRing.js';
import { Spinner } from '../internal/Spinner.js';
import { VisuallyHidden } from '../internal/VisuallyHidden.js';
import { renderIconSlot, type IconSlot } from '../internal/iconSlot.js';
import { chromeButtonRole } from '../internal/consoleRoles.js';
import { RECON_HOVER_MS, RECON_PRESS_MS, reconRole } from '../internal/reconRoles.js';
import {
  DRAWER_BTN_FONT,
  DRAWER_BTN_FONT_SM,
  DRAWER_BTN_GAP,
  DRAWER_BTN_H,
  DRAWER_BTN_H_SM,
  DRAWER_BTN_ICON,
  DRAWER_BTN_PX,
  DRAWER_BTN_PX_SM,
  DRAWER_BTN_R,
  drawerButtonCss,
} from '../internal/drawerButton.js';
import { ACTION_FONT, ACTION_GAP, ACTION_PX, ACTION_R, actionButtonCss } from '../internal/actionButton.js';
import { shopButtonCss } from '../internal/shopButton.js';
import { WEDGE_AUTH_BUTTON_CSS, authButtonOptions, type WedgeAuthSize, type WedgeAuthMotion, type WedgeAuthHover, type WedgeAuthSurface } from '../internal/wedgeAuthButton.js';
import {WEDGE_SIGNIN_BUTTON_CSS, wedgeSigninStyle, type WedgeSigninLook} from '../internal/wedgeSigninButton.js';
import {WEDGE_EMAIL_BUTTON_CSS, wedgeEmailStyle, type WedgeEmailOptions} from '../internal/wedgeEmailButton.js';
import {wedgeNoticeStyle, type WedgeNoticeLook} from '../internal/wedgeNoticeButton.js';
import {wedgeRedirectStyle, type WedgeRedirectLook} from '../internal/wedgeRedirectButton.js';
import {WEDGE_ACCOUNT_BUTTON_CSS, wedgeAccountStyle, type WedgeAccountLook} from '../internal/wedgeAccountButton.js';
import { marketingResourceChoiceCss } from '../internal/marketingResourceChoice.js';
import { marketingContactButtonCss } from '../internal/marketingContactButton.js';
import { marketingHeaderButtonCss } from '../internal/marketingHeaderButton.js';
import { marketingResourceUtilityCss } from '../internal/marketingResourceUtility.js';
import { marketingResourceButtonCss } from '../internal/marketingResourceButton.js';
import { marketingDialogButtonCss } from '../internal/marketingDialogButton.js';
import { marketingThemeOptionCss } from '../internal/marketingTheme.js';
import { RECON_RESIDUAL_BUTTON_CSS } from '../internal/reconResidualButton.js';
import { MARKETING_ICON, MARKETING_ICON_WEIGHT, marketingButtonCss } from '../internal/marketingButton.js';

/* 'console': the owner console button (legacy .btn), from the products.consoles chrome button roles. 'drawer': the
   owner console Users drawer button (legacy .dv-btn), from the --console-dv-* roles. 'action': the owner console To-do
   action button (legacy .tdbtn), from base tokens. 'shop': the storefront CTA (legacy shop .btn-solid / .btn-ghost).
   'shop-buy': the storefront buy button (legacy shop .buy.live / .buy.pending); size="sm" is the compact sticky-bar
   buy. Both shop looks read the --shop-* roles in products.shop. 'recon-cta', 'recon-ghost', 'recon-link': the shipped
   Recon .cta, .ghost-btn and .link-btn, from the products.recon roles. */
export type ButtonVariant =
  | 'primary'
  | 'gold'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'dark'
  | 'danger'
  | 'console'
  | 'drawer'
  | 'action'
  | 'shop'
  | 'shop-buy'
  | 'recon-cta'
  | 'recon-ghost'
  | 'recon-link'
  | 'recon-outcome'
  | 'recon-account'
  | 'recon-menu'
  | 'recon-surface'
  | 'recon-audit-row'
  | 'recon-entity'
  | 'recon-photo'
  | 'recon-invoice'
  | 'recon-lock'
  | 'recon-sku'
  | 'recon-disclosure'
  | 'recon-table-label'
  | 'recon-google'
  | 'recon-scan'
  | 'recon-site-primary'
  | 'recon-site-ghost'
  | 'recon-site-header'
  | 'marketing-cta'
  | 'marketing-ghost'
  | 'marketing-theme-option'
  | 'marketing-dialog-cta'
  | 'marketing-dialog-quiet'
  | 'marketing-dialog-chip'
  | 'marketing-contact-mode'
  | 'marketing-contact-quiet'
  | 'marketing-contact-submit'
  | 'marketing-resource'
  | 'marketing-resource-choice'
  | 'marketing-resource-utility'
  | 'marketing-header'
  | 'marketing-pricing-faq-tab'
  | 'marketing-pricing-faq-nav'
  | 'marketing-pricing-faq-accordion';
/* 'dense': consoles only, desktop only (legacy owner .btn.sm, 28px). Grows to TOUCH_MIN at 860px and below. */
export type ButtonSize = 'dense' | 'sm' | 'md' | 'lg';
/* variant="console": legacy .btn, .btn.accent, .modal .btn.danger (primary and tint draw as neutral there).
   variant="drawer": legacy .dv-btn, .acc (accent), .dng (danger), .pri (primary), .tint.
   variant="action": legacy .tdbtn, .pri (accent) and .quiet; other tones draw as neutral.
   tone="purple" (console only): the legacy Meetings .mtconf Confirm button, a purple edge at 45% and purple ink; hover and
   disabled keep the neutral look, as the legacy .btn:hover rule outranks it.
   variant="recon-ghost": .ghost-btn, .warn, .danger. variant="recon-link": .link-btn (neutral) and .link-btn.plain
   (accent).
   variant="marketing-cta" / "marketing-ghost": the shipped marketing CTAButton primary and ghost. Only size="md" (the
   small CTA) and "lg" (the large one) are supported; "sm" and "dense" are not; `iconEnd` is the trailing arrow, which nudges on hover.
   variant="shop" / "shop-buy": primary is the amber fill, neutral the ghost (shop) or pending buy (shop-buy). */
export type ButtonTone = 'neutral' | 'accent' | 'warn' | 'danger' | 'primary' | 'tint' | 'quiet' | 'purple';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'style'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /* Resource choices only. Omitted retains the request and provision tier geometry. */
  resourceChoiceLayout?: 'scope' | 'plan';
  /* Released public header disclosures only. Other variants ignore it. */
  headerLook?: 'more' | 'signin';
  /* Exact bare public Resource actions. Other variants ignore this option. */
  resourceUtilityLook?: 'retry' | 'back' | 'notes' | 'defaults';
  /* Wedge: exact prototype kinds. Wedge-auth: native helpers. Wedge-signin: native provider looks on primary. */
  appearance?: 'wedge' | 'wedge-auth' | 'wedge-signin' | 'wedge-account' | 'wedge-email' | 'wedge-notice' | 'wedge-redirect';
  /* Exact email looks with state restricted by look. Native attributes remain explicit. */
  email?: WedgeEmailOptions;
  /* Native notice looks on primary. The caller owns type, attributes, children and placement. */
  noticeLook?: WedgeNoticeLook;
  /* Native redirect anchor paint. The caller owns href, children, events and host font. */
  redirectLook?: WedgeRedirectLook;
  /* Wedge-signin primary only. Native provider paint; caller owns logos, labels and layout margins. */
  signInLook?: WedgeSigninLook;
  /* Wedge-signin only. Paints cursor/opacity; infers no disabled, loading or ARIA state. */
  signInPending?: boolean;
  /* Wedge-account primary only. Retains released native account controls and omitted font properties. */
  accountLook?: WedgeAccountLook;
  /* Visual cursor/opacity only. Native disabled, busy attributes and type stay explicit caller props. */
  accountPending?: boolean;
  /* Exact native auth geometry. Each name applies only to its released primary, secondary or ghost role. */
  authSize?: WedgeAuthSize;
  /* Omitted keeps helper timing. Instant retains initial computed transition values; color is ghost-only. */
  authMotion?: WedgeAuthMotion;
  /* Quiet is the secondary ghost hover. None retains controls with no shipped hover class. */
  authHover?: WedgeAuthHover;
  /* Wedge-auth primary only. Flat keeps the released recovery surface without an elevation shadow. */
  authSurface?: WedgeAuthSurface;
  /* Wedge-auth primary only: cursor for a native disabled control. Does not change disabled or loading. */
  authState?: 'invalid' | 'pending';
  /* variant="console", "drawer", "action", "shop", "shop-buy", "recon-ghost" and "recon-link" only; other variants ignore it. */
  tone?: ButtonTone;
  /* Leading icon. */
  icon?: IconSlot;
  /* Trailing icon. */
  iconEnd?: IconSlot;
  full?: boolean;
  /* Spinner in the leading slot, aria-busy, blocks onClick. */
  loading?: boolean;
  disabled?: boolean;
  /* Renders an <a>. */
  href?: string;
  target?: string;
  rel?: string;
  /* Escape hatch for router links; receives every prop. */
  component?: ElementType;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
}

/* verbatim wedge, off-scale */
const WEDGE_ROLES = ['accent', 'onAccent', 'gold', 'onGold', 'accentTint', 'surface', 'label', 'separatorBold', 'bg', 'font', 'ease'] as const;
const wedgePaint = (value: string): string => {
  const name = WEDGE_ROLES.find((role) => T[role] === value);
  if (!name) return value;
  return name === 'onAccent'
    ? wedgeRole(name, wedgeRole('bg', undefined, { exactLegacy: true }))
    : wedgeRole(name, undefined, { exactLegacy: true });
};

const PAD_SM = 14;
const PAD_MD = 22;
const PRESS_SCALE = 0.97;
const PRESS_RING = 4;
const PRESS_RING_MIX = 24;
const DISABLED_OPACITY = 0.4;
const HAIRLINE = 0.5;
/* legacy owner .btn.sm, off-scale */
const DENSE_H = 28;
const DENSE_PX = 11;
const DENSE_PY = 6;
const DENSE_GAP = 6;
const DENSE_ICON = 14;
/* legacy owner .btn, off-scale */
const CONSOLE_MS = 180;
const CONSOLE_ICON_WEIGHT = 2;
const CONSOLE_PRESS = 'translateY(1px) scale(.97)';
/* shipped Recon .cta and .ghost-btn, off-scale */
const RECON_CTA_H = 64;
const RECON_CTA_ICON = 26;

const SIZES: Record<ButtonSize, { h: number; px: number; py?: number; font: number; r: number; icon: number }> = {
  dense: { h: DENSE_H, px: DENSE_PX, py: DENSE_PY, font: font.caption, r: radius.md, icon: DENSE_ICON },
  sm: { h: control.sm, px: PAD_SM, font: font.body, r: radius.md, icon: 18 },
  md: { h: control.md, px: PAD_MD, font: font.headline, r: radius.xl, icon: 20 },
  lg: { h: control.lg, px: space[7], font: font.title3, r: radius.card, icon: 20 },
};

/* `ring` is the fill the press halo derives from; only the raised fills carry an idle shadow and a halo. */
const VARIANTS: Record<ButtonVariant, { bg: string; color: string; border: string; ring?: string }> = {
  primary: { bg: T.accent, color: T.onAccent, border: 'transparent', ring: T.accent },
  gold: { bg: T.gold, color: T.onGold, border: 'transparent', ring: T.gold },
  secondary: { bg: T.accentTint, color: T.accentInk, border: 'transparent' },
  tertiary: { bg: T.surface, color: T.label, border: T.separatorBold },
  ghost: { bg: 'transparent', color: T.accent, border: 'transparent' },
  dark: { bg: T.label, color: T.bg, border: 'transparent' },
  danger: { bg: T.red, color: T.onRed, border: 'transparent', ring: T.red },
  console: { bg: chromeButtonRole('btn', T.surface), color: T.label, border: chromeButtonRole('btn-edge', T.separator) },
  /* Painted by drawerButtonCss; the inline style leaves fill, ink and edge to the sheet. */
  drawer: { bg: T.surface, color: T.label, border: T.separatorBold },
  /* Painted by actionButtonCss, as drawer. */
  action: { bg: T.surfaceAlt, color: T.label, border: T.separator },
  /* Painted entirely by shopButtonCss; the inline style is the caller style only. */
  shop: { bg: T.amber, color: T.onGold, border: 'transparent' },
  'shop-buy': { bg: T.amber, color: T.onGold, border: T.amber },
  'recon-cta': { bg: reconRole('cta-fill', T.accent), color: T.onAccent, border: 'transparent' },
  'recon-ghost': { bg: T.surfaceAlt, color: T.label, border: T.separatorBold },
  'recon-account': { bg: T.surface, color: T.label, border: T.separator },
  'recon-menu': { bg: T.surface, color: T.label, border: T.separator },
  'recon-surface': { bg: T.surface, color: T.label, border: T.separator },
  'recon-audit-row': { bg: T.surface, color: T.label, border: T.separator },
  'recon-entity': { bg: T.surface, color: T.label, border: T.separator },
  'recon-photo': { bg: T.surface, color: T.label, border: T.separator },
  'recon-invoice': { bg: T.surface, color: T.label, border: T.separator },
  'recon-lock': { bg: T.surface, color: T.label, border: T.separator },
  'recon-sku': { bg: T.surface, color: T.label, border: T.separator },
  'recon-disclosure': { bg: T.surface, color: T.label, border: T.separator },
  'recon-google': { bg: T.surface, color: T.label, border: T.separator },
  'recon-scan': { bg: T.surface, color: T.label, border: T.separator },
  'recon-site-primary': { bg: T.surface, color: T.label, border: T.separator },
  'recon-site-ghost': { bg: T.surface, color: T.label, border: T.separator },
  'recon-site-header': { bg: T.surface, color: T.label, border: T.separator },
  'recon-table-label': { bg: T.surface, color: T.label, border: T.separator },
  'recon-outcome': { bg: T.surface, color: T.label, border: T.separatorBold },
  'recon-link': { bg: 'none', color: T.amberInk, border: 'transparent' },
  /* Painted entirely by marketingButtonCss; the inline style is `full` width and the caller style only. */
  'marketing-cta': { bg: T.accent, color: T.bg, border: 'transparent' },
  'marketing-ghost': { bg: T.surfaceAlt, color: T.label, border: T.separatorBold },
  'marketing-pricing-faq-tab': { bg: T.surface, color: T.label, border: T.separator },
  'marketing-pricing-faq-nav': { bg: T.surface, color: T.labelSec, border: T.separator },
  'marketing-pricing-faq-accordion': { bg: 'transparent', color: T.label, border: 'transparent' },
  'marketing-theme-option': { bg: T.bg, color: T.label, border: 'transparent' },
  'marketing-dialog-cta': { bg: T.accent, color: T.onAccent, border: 'transparent' },
  'marketing-dialog-quiet': { bg: 'transparent', color: T.labelSec, border: 'transparent' },
  'marketing-dialog-chip': { bg: 'transparent', color: T.labelSec, border: T.separator },
  'marketing-resource': { bg: T.accentSoft, color: T.accent, border: T.accent },
  'marketing-contact-mode': { bg: 'transparent', color: T.labelSec, border: T.separator },
  'marketing-contact-quiet': { bg: 'transparent', color: T.labelSec, border: T.separator },
  'marketing-contact-submit': { bg: T.accent, color: T.bg, border: 'transparent' },
  'marketing-resource-choice': { bg: 'transparent', color: T.labelSec, border: T.separator },
  'marketing-header': { bg: 'transparent', color: T.labelSec, border: 'transparent' },
  'marketing-resource-utility': { bg: 'transparent', color: T.labelTer, border: 'transparent' },
};
const DRAWER_SEL = '[data-rv-button][data-rv-variant="drawer"]';
const ACTION_SEL = '[data-rv-button][data-rv-variant="action"]';
const SHOP_SEL = '[data-rv-button][data-rv-variant="shop"]';
const SHOP_BUY_SEL = '[data-rv-button][data-rv-variant="shop-buy"]';
const MARKETING_CTA_SEL = '[data-rv-button][data-rv-variant="marketing-cta"]';
const MARKETING_GHOST_SEL = '[data-rv-button][data-rv-variant="marketing-ghost"]';

const isReconVariant = (v: ButtonVariant): boolean => v.startsWith('recon-');

/* Console fill, ink, edge and shadow live in the sheet, not inline, so the legacy :hover and :disabled can replace
   them and a caller `style` still wins. Each tone sets the --rv-cbtn-* locals; the rules below read only those. */
const CONSOLE = '[data-rv-button][data-rv-variant="console"]';
const cbtn = (n: string): string => `var(--rv-cbtn-${n})`;
const consoleLocals = (l: Record<string, string>): string =>
  Object.entries(l)
    .map(([k, val]) => `--rv-cbtn-${k}: ${val};`)
    .join(' ');
const CONSOLE_TONES: Record<'neutral' | 'accent' | 'danger' | 'purple', Record<string, string>> = {
  neutral: {
    bg: VARIANTS.console.bg,
    ink: T.label,
    edge: VARIANTS.console.border,
    shadow: chromeButtonRole('btn-shadow', 'none'),
    hover: chromeButtonRole('btn-hover', T.surfaceAlt),
    'hover-edge': chromeButtonRole('btn-hover-edge', T.separatorBold),
    'hover-shadow': chromeButtonRole('btn-hover-shadow', 'none'),
    'hover-filter': 'none',
  },
  accent: {
    bg: chromeButtonRole('btn-accent', T.accent),
    ink: chromeButtonRole('btn-accent-ink', T.bg),
    edge: chromeButtonRole('btn-accent-edge', T.accent),
    shadow: chromeButtonRole('btn-accent-shadow', 'none'),
    hover: chromeButtonRole('btn-accent-hover', T.accent),
    'hover-edge': chromeButtonRole('btn-accent-edge', T.accent),
    'hover-shadow': chromeButtonRole('btn-accent-hover-shadow', 'none'),
    'hover-filter': chromeButtonRole('btn-accent-hover-filter', 'none'),
  },
  danger: {
    ink: chromeButtonRole('btn-danger-ink', T.amber),
    edge: chromeButtonRole('btn-danger-edge', T.amber),
    'hover-edge': chromeButtonRole('btn-danger-hover-edge', T.amber),
  },
  purple: {
    ink: T.purple,
    edge: `color-mix(in srgb, ${T.purple} 45%, transparent)`,
  },
};

/* sm stays 36px visually; the ::before grows the hit area to TOUCH_MIN. The shop-buy sm and the recon sizes are 44px
   already. */
const RECON_CTA = '[data-rv-button][data-rv-variant="recon-cta"]';

const CSS = `${focusRing('[data-rv-button]:not(:where([data-rv-appearance="wedge-auth"], [data-rv-appearance="wedge-signin"], [data-rv-appearance="wedge-account"], [data-rv-appearance="wedge-email"], [data-rv-appearance="wedge-notice"], [data-rv-appearance="wedge-redirect"])):not(:where([data-rv-variant^="marketing-pricing-faq-"]))')}
[data-rv-button][data-rv-variant="gold"]:focus-visible { outline-color: ${T.gold}; }
[data-rv-button]:not(:where([data-rv-appearance="wedge-auth"], [data-rv-appearance="wedge-signin"], [data-rv-appearance="wedge-account"], [data-rv-appearance="wedge-email"], [data-rv-appearance="wedge-notice"], [data-rv-appearance="wedge-redirect"]))[data-rv-size="sm"]:not(${SHOP_BUY_SEL}):not([data-rv-variant^="recon-"]):not(:where([data-rv-variant="marketing-resource"]))::before { content: ''; position: absolute; inset: -${(TOUCH_MIN - control.sm) / 2}px 0; }
[data-rv-button][data-rv-appearance="wedge"][data-rv-size="sm"]:not(${SHOP_BUY_SEL}):not([data-rv-variant^="recon-"]):not(:where([data-rv-variant="marketing-resource"]))::before { left: 50%; top: 50%; right: auto; bottom: auto; width: 100%; min-width: ${TOUCH_MIN}px; height: ${TOUCH_MIN}px; transform: translate(-50%, -50%); }
${DENSE_PHONE} { [data-rv-button]:not(:where([data-rv-appearance="wedge-auth"], [data-rv-appearance="wedge-signin"], [data-rv-appearance="wedge-account"], [data-rv-appearance="wedge-email"], [data-rv-appearance="wedge-notice"], [data-rv-appearance="wedge-redirect"]))[data-rv-size="dense"] { min-height: ${TOUCH_MIN}px; min-width: ${TOUCH_MIN}px; } }
/* Legacy console .btn is 40px at 860px and below (min-height:40px), not the 44px dense size. */
${DENSE_PHONE} { [data-rv-button][data-rv-variant="console"][data-rv-size="dense"] { min-height: 40px; min-width: 40px; } }
${CONSOLE} { ${consoleLocals(CONSOLE_TONES.neutral)} background: ${cbtn('bg')}; color: ${cbtn('ink')}; border: 1px solid ${cbtn('edge')}; box-shadow: ${cbtn('shadow')}; transition: ${['border-color', 'background', 'box-shadow', 'transform'].map((p) => `${p} ${CONSOLE_MS}ms ${T.ease}`).join(', ')}; }
${CONSOLE}[data-rv-tone="accent"] { ${consoleLocals(CONSOLE_TONES.accent)} }
${CONSOLE}[data-rv-tone="danger"] { ${consoleLocals(CONSOLE_TONES.danger)} }
${CONSOLE}[data-rv-tone="purple"] { ${consoleLocals(CONSOLE_TONES.purple)} }
${CONSOLE}:not([aria-disabled]):hover { border-color: ${cbtn('hover-edge')}; background: ${cbtn('hover')}; box-shadow: ${cbtn('hover-shadow')}; filter: ${cbtn('hover-filter')}; }
${CONSOLE}[aria-disabled] { background: ${chromeButtonRole('btn-disabled', cbtn('bg'))}; color: ${chromeButtonRole('btn-disabled-ink', cbtn('ink'))}; border-color: ${chromeButtonRole('btn-disabled-edge', cbtn('edge'))}; box-shadow: ${chromeButtonRole('btn-disabled-shadow', cbtn('shadow'))}; opacity: ${chromeButtonRole('btn-disabled-opacity', String(DISABLED_OPACITY))}; }
@media (prefers-reduced-motion: no-preference) { ${CONSOLE}:not([aria-disabled]):active { transform: ${CONSOLE_PRESS}; } }
${drawerButtonCss(DRAWER_SEL)}
${actionButtonCss(ACTION_SEL)}
${shopButtonCss(SHOP_SEL, SHOP_BUY_SEL)}
${marketingButtonCss(MARKETING_CTA_SEL, MARKETING_GHOST_SEL)}
${marketingThemeOptionCss('[data-rv-button][data-rv-variant="marketing-theme-option"]')}
${marketingResourceChoiceCss('[data-rv-button][data-rv-variant="marketing-resource-choice"]')}
${marketingContactButtonCss('[data-rv-button][data-rv-variant="marketing-contact-mode"]', '[data-rv-button][data-rv-variant="marketing-contact-quiet"]', '[data-rv-button][data-rv-variant="marketing-contact-submit"]')}
${marketingResourceButtonCss('[data-rv-button][data-rv-variant="marketing-resource"]')}
${marketingHeaderButtonCss('[data-rv-button][data-rv-variant="marketing-header"]')}
${marketingResourceUtilityCss('[data-rv-button][data-rv-variant="marketing-resource-utility"]')}
${marketingPricingFaqCss()}
${marketingDialogButtonCss('[data-rv-button][data-rv-variant="marketing-dialog-cta"]', '[data-rv-button][data-rv-variant="marketing-dialog-quiet"]', '[data-rv-button][data-rv-variant="marketing-dialog-chip"]')}
${RECON_CTA}:focus-within { outline: 2px solid ${T.accent}; outline-offset: 3px; }`;


/* Recon looks: the shipped Recon rules, one for one, in their shipped order and at their shipped specificity (:where()
   pads each selector to the class selector it replaces). The sheet goes in first (useStyleOnce `first`), so Recon's
   own layout classes on the same element (.confirm-row, .doc-picker, .muted-link) still win ties, as they did. The
   cta keyboard ring sits in the base sheet above: it must beat the base :focus reset, as the shipped rule beat the
   global ring. */
const rc = (sel = ''): string => `:where([data-rv-button])[data-rv-variant="recon-cta"]${sel}`;
const rg = (sel = ''): string => `:where([data-rv-button])[data-rv-variant="recon-ghost"]${sel}`;
const rl = (sel = ''): string => `:where([data-rv-button])[data-rv-variant="recon-link"]${sel}`;
const ro = (sel = ''): string => `:where([data-rv-button])[data-rv-variant="recon-outcome"]${sel}`;
const hoverMs = (props: string[]): string => props.map((p) => `${p} ${RECON_HOVER_MS}ms ${T.ease}`).join(', ');
/* Shipped .outcome has a 56px minimum, off-scale. Native padding/display remain intact. */
const RECON_OUTCOME_H = 56;
const RECON_CSS = `${ro()} { min-height: ${RECON_OUTCOME_H}px; border-radius: ${radius.lg}px; border: 1px solid ${T.separatorBold}; background: ${T.surface}; font-weight: ${weight.bold}; font-size: ${font.title3}px; transition: ${hoverMs(['transform', 'border-color', 'background'])}; }
${ro(':active')} { transform: scale(0.97); }
${ro(':disabled')} { opacity: 0.5; cursor: default; }
${ro('[data-rv-tone="accent"]')} { color: ${T.accentInk}; border-color: ${T.accent}; background: ${T.accentSoft}; }
${ro('[data-rv-tone="warn"]')} { color: ${T.amberInk}; border-color: ${T.amber}; background: ${T.amberSoft}; }
${ro('[data-rv-tone="danger"]')} { color: ${reconRole('danger-ink', T.red)}; border-color: ${T.red}; background: ${T.redSoft}; }
@media (hover: hover) { ${ro(':not(:disabled):hover')} { border-color: ${T.labelSec}; } }
${rc()} { position: relative; flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: ${space[3]}px; min-height: ${RECON_CTA_H}px; padding: 0 ${space[6]}px; border-radius: ${radius.card}px; border: 0; background: ${VARIANTS['recon-cta'].bg}; color: ${T.onAccent}; font-family: ${T.font}; font-size: ${font.title3}px; font-weight: ${weight.bold}; letter-spacing: 0.01em; text-decoration: none; box-shadow: ${reconRole('cta-shadow', T.shadowButton)}; transition: ${hoverMs(['transform', 'box-shadow', 'opacity'])}; cursor: pointer; -webkit-user-select: none; user-select: none; }
${rc(':active')} { transform: scale(0.98); }
@media (hover: hover) { ${rc(':hover')} { transform: translateY(-1px); box-shadow: ${reconRole('cta-hover-shadow', T.shadowButton)}; } }
${rc(' svg')} { width: ${RECON_CTA_ICON}px; height: ${RECON_CTA_ICON}px; }
${rc('[aria-disabled="true"]')} { opacity: 0.72; }
:where([data-rv-button][data-rv-size="sm"])[data-rv-variant="recon-cta"] { min-height: ${control.field}px; font-size: ${font.headline}px; border-radius: ${radius.lg}px; flex: none; }
${rg()} { display: inline-flex; align-items: center; justify-content: center; gap: ${space[2]}px; min-height: ${TOUCH_MIN}px; padding: 0 ${space[4]}px; border-radius: ${radius.lg}px; border: 1px solid ${T.separatorBold}; background: ${T.surfaceAlt}; font-weight: ${weight.semibold}; font-size: ${font.callout}px; color: ${T.label}; text-decoration: none; transition: ${hoverMs(['background', 'border-color', 'transform'])}; }
${rg(':active')} { transform: scale(0.97); }
@media (hover: hover) { ${rg(':hover')} { background: ${reconRole('button-hover', T.surfaceAlt)}; border-color: ${T.accent}; } }
${rg('[data-rv-tone="warn"]')} { border-color: ${reconRole('warn-edge', T.amber)}; color: ${T.amberInk}; background: ${T.amberSoft}; }
${rg('[data-rv-tone="danger"]')} { border-color: ${reconRole('danger-edge', T.red)}; color: ${reconRole('danger-ink', T.red)}; background: ${T.redSoft}; }
${rg('[disabled]')} { opacity: 0.5; cursor: default; }
${rl()} { border: 0; background: none; padding: 0 ${space[1]}px; min-height: ${TOUCH_MIN}px; color: ${T.amberInk}; font-weight: ${weight.semibold}; text-decoration: underline; text-underline-offset: 3px; }
${rc(':active')}, ${rg(':active')} { transition-duration: ${RECON_PRESS_MS}ms; }
${rl('[data-rv-tone="accent"]')} { display: inline-flex; align-items: center; gap: ${space[1]}px; color: ${T.accentInk}; text-decoration: none; font-size: ${font.callout}px; padding: 0 ${space[2]}px; border-radius: ${radius.sm}px; }
@media (hover: hover) { ${rl('[data-rv-tone="accent"]:hover')} { background: ${T.surfaceAlt}; } }
a${rl()} { display: inline-flex; align-items: center; }
button${rc('[disabled]')} { opacity: 0.55; box-shadow: none; cursor: default; transform: none; }`;

export const Button = forwardRef<HTMLElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    resourceChoiceLayout,
    resourceUtilityLook,
    headerLook = 'more',
    appearance,
    signInLook = 'web-google',
    signInPending = false,
    email,
    noticeLook = 'in-app-open',
    redirectLook = 'copy-code',
    accountLook = 'solid',
    accountPending = false,
    authSize,
    authState,
    authMotion,
    authHover,
    authSurface,
    tone = 'neutral',
    icon,
    iconEnd,
    full = false,
    loading = false,
    disabled = false,
    href,
    target,
    rel,
    component,
    children,
    className,
    style,
    type,
    onClick,
    onPointerDown,
    onPointerUp,
    onPointerLeave,
    onPointerCancel,
    onFocus,
    onBlur,
    ...rest
  },
  ref,
) {
  useStyleOnce('button', CSS);
  useStyleOnce('button-wedge-auth', WEDGE_AUTH_BUTTON_CSS);
  useStyleOnce('button-wedge-signin', WEDGE_SIGNIN_BUTTON_CSS);
  useStyleOnce('button-wedge-account', WEDGE_ACCOUNT_BUTTON_CSS);
  useStyleOnce('button-wedge-email', WEDGE_EMAIL_BUTTON_CSS);
  useStyleOnce('button-recon-residual', RECON_RESIDUAL_BUTTON_CSS, { first: true });
  useStyleOnce('button-recon', RECON_CSS, { first: true });
  const reduced = usePrefersReducedMotion();
  const [press, setPress] = useState(false);
  const [focused, setFocused] = useState(false);
  const isWedge = appearance === 'wedge' && ['primary', 'gold', 'secondary', 'tertiary', 'ghost', 'dark'].includes(variant);
  const s = SIZES[size];
  const v = VARIANTS[variant];
  const isConsole = variant === 'console';
  const isDrawer = variant === 'drawer';
  const isAction = variant === 'action';
  /* The shop looks paint every property in their sheet, layout included, so the inline style is the caller's only. */
  const isShop = variant === 'shop' || variant === 'shop-buy';
  /* The console looks paint fill, ink, edge, opacity and press in their rule sheets. */
  const isMarketing = variant.startsWith('marketing-');
  const sheetPainted = isConsole || isDrawer || isAction;
  const drawerSm = isDrawer && size === 'dense';
  const iconWeight = sheetPainted ? CONSOLE_ICON_WEIGHT : isMarketing ? MARKETING_ICON_WEIGHT : undefined;
  const iconSize = isDrawer ? DRAWER_BTN_ICON : isMarketing ? MARKETING_ICON[size === 'lg' ? 'lg' : 'md'] : s.icon;
  const isRecon = isReconVariant(variant);
  const blocked = disabled || loading;
  const pressed = press && (isWedge || !blocked);
  const Comp: ElementType = component ?? (href !== undefined ? 'a' : 'button');
  const isNativeButton = Comp === 'button';
  const newTab = target === '_blank';

  const release = (handler?: (e: PointerEvent<HTMLButtonElement>) => void) => (e: PointerEvent<HTMLButtonElement>) => {
    setPress(false);
    handler?.(e);
  };

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (blocked) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  const semantics = isNativeButton
    ? { type: type ?? 'button', disabled: disabled || undefined }
    : Object.fromEntries(
        Object.entries({ href, target, rel: rel ?? (newTab ? 'noopener noreferrer' : undefined), tabIndex: disabled ? -1 : undefined }).filter(
          ([, val]) => val !== undefined,
        ),
      );

  /* Released provider controls retain native children and handlers, with no generic press or focus mutation. */
  if (appearance === 'wedge-signin' && variant === 'primary') {
    return (
      <Comp
        ref={ref}
        data-rv-button=""
        data-rv-variant={variant}
        data-rv-appearance="wedge-signin"
        data-rv-signin-look={signInLook}
        className={className}
        aria-busy={loading || undefined}
        {...(isNativeButton ? {type, disabled: disabled || undefined} : {href, target, rel, tabIndex:disabled ? -1 : undefined})}
        {...rest}
        onClick={isNativeButton && !loading ? onClick : handleClick}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onPointerCancel={onPointerCancel}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{...wedgeSigninStyle(signInLook, signInPending), ...style}}
      >
        {renderIconSlot(icon, s.icon)}
        {children}
        {renderIconSlot(iconEnd, s.icon)}
      </Comp>
    );
  }

  /* Released account controls retain native children and handlers, with no generic press or focus mutation. */
  if (appearance === 'wedge-account' && variant === 'primary') {
    return (
      <Comp
        ref={ref}
        data-rv-button=""
        data-rv-variant={variant}
        data-rv-appearance="wedge-account"
        data-rv-account-look={accountLook}
        className={className}
        aria-busy={loading || undefined}
        {...(isNativeButton ? {type, disabled: disabled || undefined} : {href, target, rel, tabIndex:disabled ? -1 : undefined})}
        {...rest}
        onClick={isNativeButton && !loading ? onClick : handleClick}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onPointerCancel={onPointerCancel}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{...wedgeAccountStyle(accountLook, accountPending), ...style}}
      >
        {renderIconSlot(icon, s.icon)}
        {children}
        {renderIconSlot(iconEnd, s.icon)}
      </Comp>
    );
  }

  /* Released email controls retain native children and handlers, with no generic press or focus mutation. */
  if (appearance === 'wedge-email' && variant === 'primary') {
    return (
      <Comp
        ref={ref}
        data-rv-button=""
        data-rv-variant={variant}
        data-rv-appearance="wedge-email"
        data-rv-email-look={email?.look ?? 'send'}
        className={className}
        aria-busy={loading || undefined}
        {...(isNativeButton ? {type, disabled: disabled || undefined} : {href, target, rel, tabIndex:disabled ? -1 : undefined})}
        {...rest}
        onClick={isNativeButton && !loading ? onClick : handleClick}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onPointerCancel={onPointerCancel}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{...wedgeEmailStyle(email), ...style}}
      >
        {renderIconSlot(icon, s.icon)}
        {children}
        {renderIconSlot(iconEnd, s.icon)}
      </Comp>
    );
  }

  /* Released redirect anchors preserve their native href, host font and omitted focus/press declarations. */
  if (appearance === 'wedge-redirect' && variant === 'primary') {
    return (
      <Comp
        ref={ref}
        data-rv-button=""
        data-rv-variant={variant}
        data-rv-appearance="wedge-redirect"
        data-rv-redirect-look={redirectLook}
        className={className}
        aria-busy={loading || undefined}
        {...(isNativeButton ? {type, disabled: disabled || undefined} : {href, target, rel, tabIndex:disabled ? -1 : undefined})}
        {...rest}
        onClick={isNativeButton && !loading ? onClick : handleClick}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onPointerCancel={onPointerCancel}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{...wedgeRedirectStyle(redirectLook), ...style}}
      >
        {renderIconSlot(icon, s.icon)}
        {children}
        {renderIconSlot(iconEnd, s.icon)}
      </Comp>
    );
  }

  /* Released notice controls retain native children and handlers, with no generic press or focus mutation. */
  if (appearance === 'wedge-notice' && variant === 'primary') {
    return (
      <Comp
        ref={ref}
        data-rv-button=""
        data-rv-variant={variant}
        data-rv-appearance="wedge-notice"
        data-rv-notice-look={noticeLook}
        className={className}
        aria-busy={loading || undefined}
        {...(isNativeButton ? {type, disabled: disabled || undefined} : {href, target, rel, tabIndex:disabled ? -1 : undefined})}
        {...rest}
        onClick={isNativeButton && !loading ? onClick : handleClick}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onPointerCancel={onPointerCancel}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{...wedgeNoticeStyle(noticeLook), ...style}}
      >
        {renderIconSlot(icon, s.icon)}
        {children}
        {renderIconSlot(iconEnd, s.icon)}
      </Comp>
    );
  }

  /* Wedge splash helpers use native children, focus, pointer behavior and an omitted type. */
  if (appearance === 'wedge-auth' && ['primary', 'secondary', 'ghost'].includes(variant)) {
    const authOptions = authButtonOptions(variant, authSize, authMotion, authHover, authSurface);
    return (
      <Comp
        ref={ref}
        data-rv-button=""
        data-rv-variant={variant}
        data-rv-appearance="wedge-auth"
        data-rv-auth-size={authOptions.size}
        data-rv-auth-motion={authOptions.motion}
        data-rv-auth-hover={authOptions.hover}
        data-rv-auth-surface={authOptions.surface}
        data-rv-auth-state={variant === 'primary' ? authState : undefined}
        className={className}
        aria-busy={loading || undefined}
        aria-disabled={blocked || undefined}
        {...(isNativeButton ? {type, disabled: disabled || undefined} : {href, target, rel, tabIndex:disabled ? -1 : undefined})}
        {...rest}
        onClick={isNativeButton && !loading ? onClick : handleClick}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onPointerCancel={onPointerCancel}
        onFocus={onFocus}
        onBlur={onBlur}
        style={style}
      >
        {renderIconSlot(icon, s.icon)}
        {children}
        {renderIconSlot(iconEnd, s.icon)}
      </Comp>
    );
  }

  return (
    <Comp
      ref={ref}
      data-rv-button=""
      data-rv-variant={variant}
      data-rv-resource-choice-layout={variant === 'marketing-resource-choice' ? resourceChoiceLayout : undefined}
      data-rv-header-look={variant === 'marketing-header' ? headerLook : undefined}
      data-rv-resource-utility={variant === 'marketing-resource-utility' ? resourceUtilityLook : undefined}
      data-rv-size={size}
      data-rv-appearance={isWedge ? appearance : undefined}
      data-rv-tone={sheetPainted || isShop || variant === 'marketing-resource' || variant === 'recon-ghost' || variant === 'recon-link' || variant === 'recon-outcome' ? tone : undefined}
      className={className}
      aria-busy={loading || undefined}
      aria-disabled={blocked || undefined}
      {...semantics}
      {...rest}
      onClick={handleClick}
      onPointerDown={(e: PointerEvent<HTMLButtonElement>) => {
        if (isWedge || !blocked) setPress(true);
        onPointerDown?.(e);
      }}
      onPointerUp={release(onPointerUp)}
      onPointerLeave={release(onPointerLeave)}
      /* The legacy Wedge keeps press on cancel until pointer up or leave. */
      onPointerCancel={isWedge ? onPointerCancel : release(onPointerCancel)}
      onFocus={(e: FocusEvent<HTMLButtonElement>) => { if (isWedge) setFocused(true); onFocus?.(e); }}
      onBlur={(e: FocusEvent<HTMLButtonElement>) => { if (isWedge) setFocused(false); onBlur?.(e); }}
      /* A shop or recon look lives wholly in its sheet: no inline style but the caller's, so the app's layout classes still
         apply. */
      style={isMarketing ? { width: full ? '100%' : undefined, ...style } : isShop || isRecon ? style : {
        position: 'relative',
        boxSizing: 'border-box',
        /* Console sizes from its padding like legacy .btn: 28px with an icon (line-height 1, legacy .btn:has(.bic)),
           text-only takes the font's normal line height (32px dense). */
        height: isConsole || isAction ? 'auto' : isDrawer ? (drawerSm ? DRAWER_BTN_H_SM : DRAWER_BTN_H) : s.h,
        /* Legacy .tdbtn is font:inherit, so the line height comes from the parent. */
        lineHeight: isAction ? 'inherit' : isConsole ? (icon != null || iconEnd != null || loading ? 1 : 'normal') : isDrawer ? 'normal' : undefined,
        minWidth: size === 'sm' && !isDrawer && !isWedge ? TOUCH_MIN : undefined,
        width: full ? '100%' : 'auto',
        padding: isAction ? `0 ${ACTION_PX}px` : isDrawer ? `0 ${drawerSm ? DRAWER_BTN_PX_SM : DRAWER_BTN_PX}px` : `${s.py ?? 0}px ${s.px}px`,
        margin: 0,
        background: sheetPainted ? undefined : isWedge ? wedgePaint(v.bg) : v.bg,
        color: sheetPainted ? undefined : isWedge ? wedgePaint(variant === 'secondary' ? T.accent : v.color) : v.color,
        border: sheetPainted ? undefined : `${size === 'dense' ? 1 : HAIRLINE}px solid ${isWedge ? wedgePaint(v.border) : v.border}`,
        borderRadius: isAction ? ACTION_R : isDrawer ? DRAWER_BTN_R : s.r,
        fontFamily: isWedge ? wedgePaint(T.font) : T.font,
        fontSize: isAction ? ACTION_FONT : isDrawer ? (drawerSm ? DRAWER_BTN_FONT_SM : DRAWER_BTN_FONT) : isConsole ? chromeButtonRole('btn-size', `${s.font}px`) : s.font,
        /* Legacy .dv-btn.pri is bold; the console accent reads an optional weight role; every other look is semibold. */
        fontWeight: (isAction
          ? tone === 'accent'
            ? weight.semibold
            : `var(--chrome-body-weight, ${weight.regular})`
          : isDrawer && tone === 'primary'
          ? weight.bold
          : isConsole && tone === 'accent'
            ? chromeButtonRole('btn-accent-weight', String(weight.semibold))
            : weight.semibold) as CSSProperties['fontWeight'],
        /* Legacy .btn is a native button, which resets letter-spacing to normal; as a link (href) the console look would
           inherit the parent tracking (a .empty line sets 0.3px). */
        letterSpacing: isConsole ? 'normal' : undefined,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isAction ? ACTION_GAP : isDrawer ? DRAWER_BTN_GAP : size === 'dense' ? DENSE_GAP : space[2],
        cursor: disabled ? 'not-allowed' : loading ? 'progress' : 'pointer',
        opacity: sheetPainted ? undefined : disabled ? DISABLED_OPACITY : 1,
        transform: sheetPainted ? undefined : pressed && (isWedge || !reduced) ? `scale(${PRESS_SCALE})` : 'scale(1)',
        boxShadow: v.ring
          ? pressed
            ? `0 0 0 ${PRESS_RING}px color-mix(in srgb, ${isWedge ? wedgePaint(v.ring) : v.ring} ${PRESS_RING_MIX}%, transparent)`
            : isWedge && focused
              ? `0 0 0 2px ${wedgePaint(T.bg)}, 0 0 0 3px ${wedgePaint(v.ring)}`
              : T.shadowButton
          : undefined,
        transition: sheetPainted
          ? undefined
          : ['transform', 'background', 'box-shadow'].map((p) => `${p} ${duration.press}ms ${isWedge ? wedgePaint(T.ease) : T.ease}`).join(', '),
        touchAction: isWedge ? undefined : 'manipulation',
        WebkitTapHighlightColor: isWedge ? undefined : 'transparent',
        ...style,
      }}
    >
      {loading ? <Spinner size={16} stroke={2} /> : isWedge ? icon : renderIconSlot(icon, iconSize, undefined, iconWeight)}
      {children}
      {renderIconSlot(iconEnd, iconSize, undefined, iconWeight)}
      {newTab && variant !== 'marketing-resource' ? <VisuallyHidden> (opens in a new tab)</VisuallyHidden> : null}
    </Comp>
  );
});
